
import { Auth } from "aws-amplify";

const BASE = process.env.REACT_APP_FASTAPI_URL;


export const getUser = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser(); // Await the promise
    return user.username; // Return the username
  } catch (err) {
    console.log("Error in getUser: ", err);
    return null; // Handle the case where user is not authenticated
  }
};


/** ---------- helpers ---------- */

const assertOk = async (res) => {
  if (!res.ok) {
    let detail = "";
    try {
      // FastAPI often returns {detail: ...}
      const err = await res.json();
      detail = err?.detail ? ` | ${JSON.stringify(err.detail)}` : ` | ${JSON.stringify(err)}`;
    } catch {
      // ignore
    }
    throw new Error(`HTTP error! Status: ${res.status}${detail}`);
  }
};

const toJson = async (res) => {
  await assertOk(res);
  return res.json();
};

const buildQS = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    qs.append(k, String(v));
  });
  return qs.toString();
};

/** ---------- Projects ---------- */
/**
 * 1) List all projects for a user
 * GET /artifact-projects?username=...
 */
export const listProjects = async () => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(`${BASE}/artifact-projects?${qs}`, { method: "GET" });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error listing projects: ${error.message}`);
  }
};

/**
 * 2) Create a new project
 * POST /artifact-projects  (JSON in your backend)
 * body: { username, name, description, users? }
 */
export const createProject = async ({ name, description = "", users = null }) => {
  const username = await getUser();

  try {
    const res = await fetch(`${BASE}/artifact-projects`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username,
        name,
        description,
        users: Array.isArray(users) ? users : [username],
      }),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error creating project: ${error.message}`);
  }
};

/**
 * 3) Get project details
 * GET /artifact-projects/{project_id}?username=...
 */
export const getProject = async (projectId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}?${qs}`, {
      method: "GET",
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error fetching project details: ${error.message}`);
  }
};

/**
 * Hard delete a project
 * DELETE /artifact-projects/{project_id}/hard?username=...
 */
export const hardDeleteProject = async (projectId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/hard?${qs}`, {
      method: "DELETE",
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error hard deleting project: ${error.message}`);
  }
};

/**
 * Clone a project including all its inputs and artifacts
 * POST /artifact-projects/{project_id}/clone
 * body: { username }
 */
export const cloneProject = async (projectId) => {
  const username = await getUser();
  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/clone`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error cloning project: ${error.message}`);
  }
};

/**
 * Update project details (name and/or description)
 * PUT /artifact-projects/{project_id}
 * body: { username, name?, description? }
 */
export const updateProject = async (projectId, { name = null, description = null }) => {
  const username = await getUser();

  // Build update payload with only provided fields
  const payload = { username };
  if (name !== null) payload.name = name;
  if (description !== null) payload.description = description;

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error updating project: ${error.message}`);
  }
};

/** ---------- Inputs (instructions + files) ---------- */

/**
 * List inputs (instructions + files)
 * GET /artifact-projects/{project_id}/inputs?username=...
 */
export const listInputs = async (projectId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/inputs?${qs}`, {
      method: "GET",
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error listing inputs: ${error.message}`);
  }
};

/**
 * 5) Add instructions for pipeline
 * POST /artifact-projects/{project_id}/inputs/instructions  (multipart)
 * form: username, instructions_md_file (Blob/File)
 */
export const uploadInstructions = async (projectId, markdown, pipeline_stage) => {
  const username = await getUser();
  console.log("uploadInstructions called with:", { projectId, pipeline_stage, markdownLength: markdown?.length });
  try {
    const payload = { username, markdown, pipeline_stage };
    console.log("Sending payload:", payload);
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/inputs/instructions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error uploading instructions: ${error.message}`);
  }
};

/**
 * 6) Add files for pipeline
 * POST /artifact-projects/{project_id}/inputs/files  (multipart)
 * form: username, files (repeat)
 */
export const uploadPipelineFiles = async (projectId, files) => {
  const username = await getUser();
  const formData = new FormData();
  formData.append("username", username);

  // files: FileList | File[]
  Array.from(files).forEach((f) => formData.append("files", f));

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/inputs/files`, {
      method: "POST",
      body: formData,
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error uploading pipeline files: ${error.message}`);
  }
};

/**
 * Delete input files
 * DELETE /artifact-projects/{project_id}/inputs/files
 * body: { username, input_file_ids: [...] }
 * (Your backend uses JSON body here.)
 */
export const deleteInputFiles = async (projectId, inputFileIds) => {
  const username = await getUser();

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/inputs/files`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username,
        input_file_ids: inputFileIds,
      }),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error deleting input files: ${error.message}`);
  }
};

/**
 * Update instructions markdown content
 * PUT /artifact-projects/{project_id}/inputs/instructions/{input_file_id}
 * body: { username, markdown }
 */
export const updateInstructions = async (projectId, inputFileId, markdown) => {
  const username = await getUser();

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/inputs/instructions/${inputFileId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, markdown }),
      }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error updating instructions: ${error.message}`);
  }
};

/** ---------- Pipelines ---------- */

/**
 * List pipelines catalog (if you added it)
 * GET /artifact-projects/{project_id}/pipelines/catalog?username=...
 */
export const listPipelinesCatalog = async (projectId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/pipelines/catalog?${qs}`, {
      method: "GET",
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error listing pipelines: ${error.message}`);
  }
};

/**
 * 4) Run any pipeline (1,2,3a,3b,3c,4,5,6)
 * POST /artifact-projects/{project_id}/pipelines/{pipeline_key}
 * body: pipeline-specific JSON (your backend expects JSON)
 *
 * payload shape typically:
 * {
 *   username,
 *   instructions_file_id,
 *   file_ids,
 *   optional ids: outline_md_artifact_id / lab_md_artifact_id ...
 * }
 */
export const runPipeline = async (projectId, pipelineKey, payload) => {
  const username = await getUser();

  try {
    console.log(`Running pipeline ${pipelineKey} with payload:`, payload)
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/pipelines/${pipelineKey}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username,
        ...payload,
      }),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error running pipeline ${pipelineKey}: ${error.message}`);
  }
};

/** ---------- Concept Labs (for case-study attachment source) ---------- */

/**
 * List concept labs for the current user.
 * GET /artifact-projects/{project_id}/pipelines/concept-labs?username=...
 */
export const listConceptLabsForArtifacts = async (projectId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/pipelines/concept-labs?${qs}`,
      { method: "GET" }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error listing concept labs: ${error.message}`);
  }
};

/**
 * Fetch previewable files from a concept lab repository.
 * GET /artifact-projects/{project_id}/pipelines/concept-labs/{lab_id}/files?username=...
 */
export const getConceptLabFilesForArtifacts = async (projectId, labId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/pipelines/concept-labs/${labId}/files?${qs}`,
      { method: "GET" }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error fetching concept lab files: ${error.message}`);
  }
};

/**
 * 7) Recompile TeX -> PDF
 * POST /artifact-projects/{project_id}/pipelines/recompile
 * body: { username, tex_content, source_artifact_ids, basename }
 */
export const recompileTexToPdf = async (projectId, { texContent, sourceArtifactIds = [], basename = "document" }) => {
  const username = await getUser();

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/pipelines/recompile`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username,
        tex_content: texContent,
        source_artifact_ids: sourceArtifactIds,
        basename,
      }),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error recompiling TeX: ${error.message}`);
  }
};

/**
 * Update TeX artifact (versioned)
 * POST /artifact-projects/{project_id}/pipelines/tex/{artifact_id}/update
 * body: { username, tex_content }
 */
export const updateTexArtifact = async (projectId, artifactId, texContent) => {
  const username = await getUser();

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/pipelines/tex/${artifactId}/update`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, tex_content: texContent }),
      }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error updating TeX artifact: ${error.message}`);
  }
};

/**
 * Update Markdown artifact (versioned)
 * POST /artifact-projects/{project_id}/pipelines/markdown/{artifact_id}/update
 * body: { username, updated_markdown }
 */
export const updateMarkdownArtifact = async (projectId, artifactId, updatedMarkdown) => {
  const username = await getUser();

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/pipelines/markdown/${artifactId}/update`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, markdown_content: updatedMarkdown }),
      }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error updating Markdown artifact: ${error.message}`);
  }
};

/**
 * Get TeX versions
 * GET /artifact-projects/{project_id}/pipelines/tex/{artifact_id}/versions?username=...
 */
export const getTexVersions = async (projectId, artifactId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/pipelines/tex/${artifactId}/versions?${qs}`,
      { method: "GET" }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error fetching TeX versions: ${error.message}`);
  }
};

/**
 * Get Markdown versions
 * GET /artifact-projects/{project_id}/pipelines/markdown/{artifact_id}/versions?username=...
 */
export const getMarkdownVersions = async (projectId, artifactId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/pipelines/markdown/${artifactId}/versions?${qs}`,
      { method: "GET" }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error fetching Markdown versions: ${error.message}`);
  }
};

/**
 * Fetch markdown content via backend (avoids CORS issues when fetching S3 public URLs from the browser)
 * POST /artifact-projects/{project_id}/inputs/fetch-markdown?public_url=...
 * Returns: { markdown: string }
 */
export const fetchMarkdownFromS3 = async (projectId, publicUrl) => {
  if (!projectId || !publicUrl) {
    throw new Error("projectId and publicUrl are required");
  }

  // The backend expects the public_url as a query parameter on a POST
  const url = `${BASE}/artifact-projects/${projectId}/inputs/fetch-markdown?public_url=${encodeURIComponent(
    publicUrl
  )}`;

  try {
    const res = await fetch(url, { method: "POST" });
    return await toJson(res); // should be { markdown: "..." }
  } catch (error) {
    throw new Error(`Error fetching markdown via backend: ${error.message}`);
  }
};

/**
 * Fetch TeX content via backend (avoids CORS issues when fetching S3 public URLs from the browser)
 * POST /artifact-projects/{project_id}/inputs/fetch-tex?public_url=...
 * Returns: backend response JSON (commonly { tex: string })
 */
export const fetchTexFromS3 = async (projectId, publicUrl) => {
  if (!projectId || !publicUrl) {
    throw new Error("projectId and publicUrl are required");
  }

  const url = `${BASE}/artifact-projects/${projectId}/inputs/fetch-tex?public_url=${encodeURIComponent(
    publicUrl
  )}`;

  try {
    const res = await fetch(url, { method: "POST" });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error fetching tex via backend: ${error.message}`);
  }
};

/** ---------- Runs ---------- */

/**
 * Create & execute a run (generic route in runs.py)
 * POST /artifact-projects/{project_id}/runs
 * (your backend takes JSON body with username + run_params typically)
 *
 * If you don't need this because you always run via /pipelines/*, you can skip.
 */
export const createRun = async (projectId, payload) => {
  const username = await getUser();

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/runs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, ...payload }),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error creating run: ${error.message}`);
  }
};

/**
 * List all runs for a project
 * GET /artifact-projects/{project_id}/runs?username=...
 */
export const listRuns = async (projectId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/runs?${qs}`, {
      method: "GET",
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error listing runs: ${error.message}`);
  }
};

/**
 * Get run status/details
 * GET /artifact-projects/{project_id}/runs/{run_id}?username=...
 */
export const getRun = async (projectId, runId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/runs/${runId}?${qs}`, {
      method: "GET",
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error fetching run: ${error.message}`);
  }
};

/** ---------- Artifacts ---------- */

/**
 * List artifacts (optionally filter by type)
 * GET /artifact-projects/{project_id}/artifacts?username=...&type=...
 */
export const listArtifacts = async (projectId, type = null) => {
  const username = await getUser();
  const qs = buildQS({ username, type });

  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/artifacts?${qs}`, {
      method: "GET",
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error listing artifacts: ${error.message}`);
  }
};

/**
 * Delete an artifact
 * DELETE /artifact-projects/{project_id}/artifacts/{artifact_id}?username=...
 */
export const deleteArtifact = async (projectId, artifactId) => {
  const username = await getUser();
  const qs = buildQS({ username });

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/artifacts/${artifactId}?${qs}`,
      { method: "DELETE" }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error deleting artifact: ${error.message}`);
  }
};

/**
 * Update artifact content
 * PUT /artifact-projects/{project_id}/artifacts/{artifact_id}
 * body: { username, content }
 */
export const updateArtifact = async (projectId, artifactId, content) => {
  const username = await getUser();

  try {
    const res = await fetch(
      `${BASE}/artifact-projects/${projectId}/artifacts/${artifactId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, content }),
      }
    );
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error updating artifact: ${error.message}`);
  }
};

/**
 * Update tags for an artifact project
 * POST /artifact-projects/{project_id}/tags
 * body: { tags }
 */
export const updateProjectTags = async (projectId, tags) => {
  try {
    const res = await fetch(`${BASE}/artifact-projects/${projectId}/tags`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tags }),
    });
    return await toJson(res);
  } catch (error) {
    throw new Error(`Error updating project tags: ${error.message}`);
  }
};